// Washing Plan (Management › Planning & Delivery): the month's washes on a calendar, the boats that still need
// one beside it, and a printed schedule in English and Greek for the people who do the washing.
//   • the boats come from the Overview (production: after the sea trial, before delivery and polishing, and
//     a re-wash when that was too long before the delivery) and the Service sheet (a finished service, before
//     the boat goes back: this season's registrations only, each boat once), by the rules in
//     features/washPlan.ts;
//   • "To plan" lists the washes not planned yet, the ones ready and due soonest first. Drag one onto a day
//     (or open it and pick the day); drag a planned wash to move it. Open a wash (components/WashDialog.tsx)
//     to tick it done, mark it as needing no wash, note it, move it or take it off the plan. The washes marked
//     "no washing required" are listed under "To plan", to undo. A production boat's washes need Overview
//     edit access, a service boat's Service edit access; everyone else sees the plan;
//   • the boats' deliveries show on their days, so the washes can be planned around them;
//   • each wash says how long it takes by the boat's model (the outside and the interior: WASH_TIMES), and
//     each day adds up its washes, so a day isn't given more washing than the floor can do;
//   • Print / PDF and Excel give the month's schedule in English, Greek or both (features/washPlanPrint.ts).
// On a phone the month is a list of days with their washes, and "To plan" is its own tab.
import { useCallback, useMemo, useState, type DragEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { REWASH_AFTER_DAYS, WASH_KINDS, WASH_KIND_INFO, sumWashTimes, washTime, washTotal, type WashKind, type WashSource } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useActiveSeason, useSeason } from '../season/SeasonContext.js';
import { useServiceSheet, type OverviewResponse } from '../features/hooks.js';
import { productionWashes, serviceWashes, useSetWash, useWashes, washBoats, washQueue, washRangeText, washesByDay, type WashItem } from '../features/washPlan.js';
import { exportWashScheduleXlsx, printWashSchedule, washLines, type WashLang } from '../features/washPlanPrint.js';
import { addMonths, monthWeeks } from '../features/serviceCalendar.js';
import { IconClock, IconDrop, IconDropOff, WashDialog, plusDays, useWashWords, washEntryOf as entryOf, type WashChange, type WashEntry as Entry } from '../components/WashDialog.js';
import { InlineError, LoadingState, PageHeader, SearchInput, Segmented, SyncLabel } from '../components/ui.js';
import { IconChevronLeft, IconChevronRight, IconDownload, IconPrinter } from '../components/icons.js';
import { api } from '../lib/api.js';
import { useDateFormatter } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { useIsPhone } from '../lib/media.js';
import { useTabState } from '../lib/tabState.js';
import { fold, hasText } from '../lib/search.js';

const LineIcon = ({ size = 14, children }: { size?: number; children: ReactNode }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);
const IconShip = ({ size }: { size?: number }) => <LineIcon size={size}><path d="M3 15l2.2 4h13.6L21 15H3z" /><path d="M6 15V9h12v6" /><path d="M12 5v4" /></LineIcon>;
const IconGrip = ({ size }: { size?: number }) => <LineIcon size={size}><circle cx="9" cy="6" r=".6" /><circle cx="15" cy="6" r=".6" /><circle cx="9" cy="12" r=".6" /><circle cx="15" cy="12" r=".6" /><circle cx="9" cy="18" r=".6" /><circle cx="15" cy="18" r=".6" /></LineIcon>;

const monthTitle = (month: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${month}-01T00:00:00Z`));
const wd = (day: string, locale: string, weekday: 'short' | 'long' = 'short') =>
  new Intl.DateTimeFormat(locale, { weekday, timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function WashingPlan() {
  const { can } = useAuth();
  const canOv = can('overview:read');
  const canSv = can('service:read');
  const editable = (s: WashSource) => (s === 'prod' ? can('overview:write') : can('service:write'));
  const season = useActiveSeason();
  // the Service sheet keeps past seasons' visits too: only this season's registrations are washed, as the
  // Service page shows them (features/serviceSeason.ts)
  const { currentSeason } = useSeason();
  const ov = useQuery({
    queryKey: ['overview', season],
    queryFn: () => api.get<OverviewResponse>(`/api/overview?season=${encodeURIComponent(season)}`),
    enabled: canOv && !!season,
  });
  const sv = useServiceSheet(canSv);
  const washes = useWashes();
  const set = useSetWash();
  const df = useDateFormatter();
  const isPhone = useIsPhone();
  const today = localTodayIso();

  const [month, setMonth] = useTabState('wash.month', today.slice(0, 7));
  const [rewashDays, setRewashDays] = useTabState('wash.rewash', REWASH_AFTER_DAYS);
  const [lang, setLang] = useTabState<WashLang>('wash.lang', 'both');
  const [filter, setFilter] = useTabState<'all' | WashSource>('wash.filter', 'all');
  const [showDeliveries, setShowDeliveries] = useTabState('wash.deliveries', true);
  const [tab, setTab] = useTabState<'month' | 'queue'>('wash.tab', 'month');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [overDay, setOverDay] = useState<string | null>(null);
  const [exportErr, setExportErr] = useState<unknown>(null);

  const records = useMemo(() => washes.data?.washes ?? [], [washes.data]);
  const recMap = useMemo(() => new Map(records.map((r) => [r.id, r])), [records]);
  const items = useMemo(
    () => [
      ...(ov.data ? productionWashes(ov.data.vessels, recMap, today, rewashDays) : []),
      ...(sv.data ? serviceWashes(sv.data, recMap, today, currentSeason) : []),
    ],
    [ov.data, sv.data, recMap, today, rewashDays, currentSeason],
  );
  const itemMap = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  // a wash's boat model (as the entry reads it), for how long it takes
  const modelOf = useCallback((r: { id: string; boatModel: string }) => itemMap.get(r.id)?.model ?? r.boatModel, [itemMap]);
  const boats = useMemo(() => washBoats(ov.data?.vessels ?? [], items), [ov.data, items]);
  const boatOf = (r: { source: WashSource; boatKey: string }) => boats.get(`${r.source}:${r.boatKey}`);
  const allQueue = useMemo(() => washQueue(items), [items]);
  // the washes someone said aren't needed, newest first, kept in view to undo
  const notNeeded = useMemo(
    () => records.filter((r) => r.notNeededOn && (filter === 'all' || r.source === filter)).sort((a, b) => b.notNeededOn.localeCompare(a.notNeededOn)),
    [records, filter],
  );
  const queue = useMemo(() => {
    const query = fold(q.trim());
    return allQueue.filter((i) => (filter === 'all' || i.source === filter) && hasText(query, `${i.name} ${i.sap} ${i.model}`));
  }, [allQueue, filter, q]);
  const byDay = useMemo(() => washesByDay(records, itemMap, month), [records, itemMap, month]);
  // the deliveries of the boats on the page, by day, so washes can be planned around them
  const deliveries = useMemo(() => {
    const seen = new Set<string>();
    const out = new Map<string, string[]>();
    for (const i of items) {
      const k = `${i.source}:${i.boatKey}`;
      if (!i.delivery.startsWith(month) || seen.has(k)) continue;
      seen.add(k);
      out.set(i.delivery, [...(out.get(i.delivery) ?? []), i.name]);
    }
    return out;
  }, [items, month]);

  const stats = useMemo(() => {
    const soon = plusDays(today, 7);
    const planned = records.filter((r) => !r.doneOn && r.plannedOn.startsWith(month));
    return {
      plannedTime: sumWashTimes(planned.map(modelOf)),
      toPlan: allQueue.length,
      ready: allQueue.filter((i) => i.ready).length,
      planned: planned.length,
      done: records.filter((r) => r.doneOn.startsWith(month)).length,
      due: allQueue.filter((i) => i.dueBy && i.dueBy <= soon).length,
      late: records.filter((r) => !r.doneOn && r.plannedOn && r.plannedOn < today).length,
    };
  }, [allQueue, records, month, today, modelOf]);

  // ── changes ──
  const change = (e: Entry, v: WashChange) =>
    set.mutate({ source: e.source, boatKey: e.boatKey, kind: e.kind, boatName: e.name.slice(0, 160), boatModel: e.model.slice(0, 60), ...v });
  const entryById = (id: string): Entry | null => {
    const item = itemMap.get(id);
    const record = recMap.get(id);
    const src = item ?? record;
    return src ? entryOf(item, record, boatOf(src)) : null;
  };
  const dropOn = (day: string) => {
    const e = drag ? entryById(drag) : null;
    setDrag(null);
    setOverDay(null);
    if (!e || !editable(e.source) || e.record?.doneOn || e.record?.plannedOn === day) return;
    change(e, { plannedOn: day });
  };
  const dragProps = (e: Entry) =>
    !isPhone && editable(e.source) && !e.record?.doneOn
      ? {
          draggable: true,
          onDragStart: (ev: DragEvent<HTMLElement>) => { ev.dataTransfer.setData('text/plain', e.id); ev.dataTransfer.effectAllowed = 'move'; setDrag(e.id); },
          onDragEnd: () => { setDrag(null); setOverDay(null); },
        }
      : {};
  const dayDrop = (day: string) =>
    drag
      ? {
          onDragOver: (ev: DragEvent<HTMLElement>) => { ev.preventDefault(); ev.dataTransfer.dropEffect = 'move'; if (overDay !== day) setOverDay(day); },
          onDragLeave: () => setOverDay((d) => (d === day ? null : d)),
          onDrop: (ev: DragEvent<HTMLElement>) => { ev.preventDefault(); dropOn(day); },
        }
      : {};

  // ── words ──
  const { fmt, whyText, due } = useWashWords(today);

  // how long a day's washes take, in full (the day itself shows it short)
  const dayTimeText = (t: ReturnType<typeof sumWashTimes>) =>
    `Washing time ${washRangeText(t.total)}: outside ${washRangeText(t.outside)}, interior ${washRangeText(t.interior)}`
    + (t.unknown ? `, and ${plural(t.unknown, 'boat')} without a set time` : '');

  const badge = (k: WashKind, size: 'sm' | 'md' = 'sm', done = false) => <span className={`wp-badge wp-k-${k} wp-badge-${size}${done ? ' is-done' : ''}`} aria-hidden="true">{WASH_KIND_INFO[k].n}</span>;
  // a long boat name breaks after its model prefix ("45_" / "POSEIDON") before it breaks anywhere else
  const breakable = (name: string) => name.split(/(?<=[_ ])/).flatMap((part, i) => (i ? [<wbr key={i} />, part] : [part]));

  // ── export ──
  const lines = () => washLines(
    records, month,
    (r) => itemMap.get(r.id)?.delivery ?? boatOf(r)?.delivery ?? '',
    (r) => itemMap.get(r.id)?.sap ?? boatOf(r)?.sap ?? (r.source === 'prod' ? r.boatKey : ''),
    modelOf,
  );
  const doPrint = () => { setExportErr(printWashSchedule(lines(), month, lang, today) ? null : new Error('The print window was blocked. Allow pop-ups for this site and try again.')); };
  const doExcel = () => { exportWashScheduleXlsx(lines(), month, lang).then(() => setExportErr(null), (e) => setExportErr(e)); };

  const loading = (canOv && ov.isLoading) || (canSv && sv.isLoading) || washes.isLoading;
  const weeks = monthWeeks(month);
  const open = openId ? entryById(openId) : null;

  // ── pieces ──
  const queueCard = (i: WashItem) => {
    const e = entryOf(i);
    const d = due(i);
    const time = washTime(i.model);
    return (
      <li key={i.id} className={`wp-qcard wp-k-${i.kind}${i.ready ? '' : ' is-wait'}${drag === i.id ? ' is-drag' : ''}`} {...dragProps(e)}>
        <button type="button" className="wp-qmain" onClick={() => setOpenId(i.id)} aria-label={`${i.name}: ${WASH_KIND_INFO[i.kind].name}. ${d.text}.${time ? ` Takes ${washRangeText(washTotal(time))}.` : ''} Open to plan it`}>
          <span className="wp-qtop">
            {badge(i.kind, 'md')}
            <span className="wp-qname">{i.name}</span>
            {!isPhone && editable(i.source) && <span className="wp-grip" title="Drag onto a day"><IconGrip size={14} /></span>}
          </span>
          <span className="wp-qmeta">
            {[i.model, i.sap, i.pct != null ? `${Math.round(i.pct)}%` : i.source === 'service' ? 'Service' : 'Last season'].filter(Boolean).join(' · ')}
            {time && (
              <span className="wp-qtime" title={`Outside ${washRangeText(time.outside)} · interior ${washRangeText(time.interior)}`}>
                <IconClock size={12} />{washRangeText(washTotal(time))}
              </span>
            )}
          </span>
          <span className="wp-qwhy">{whyText(i.why)}</span>
          <span className={`wp-due${d.tone ? ` is-${d.tone}` : ''}`}>{d.text}</span>
        </button>
      </li>
    );
  };
  const chip = (e: Entry) => {
    const done = !!e.record?.doneOn;
    const time = washTime(e.model);
    const late = !done && !!e.record?.plannedOn && e.record.plannedOn < today;
    return (
      <button
        key={e.id}
        type="button"
        className={`wp-chip wp-k-${e.kind}${done ? ' is-done' : ''}${late ? ' is-late' : ''}${drag === e.id ? ' is-drag' : ''}`}
        onClick={() => setOpenId(e.id)}
        title={`${e.name} · ${WASH_KIND_INFO[e.kind].name} · ${WASH_KIND_INFO[e.kind].nameEl}${time ? ` · ${washRangeText(washTotal(time))}` : ''}${done ? ' · done' : late ? ' · not done yet' : ''}`}
        {...dragProps(e)}
      >
        {badge(e.kind, 'sm', done)}
        <span className="wp-chip-n">{breakable(e.name)}</span>
      </button>
    );
  };

  const calendar = (
    <div className="wp-cal" role="grid" aria-label={`Washes in ${monthTitle(month, 'en-GB')}`}>
      <div className="wp-cal-head" role="row">
        {weeks[0]!.map((d) => (
          <div key={d} className="wp-cal-wd" role="columnheader">
            <span>{wd(d, 'en-GB')}</span><span className="wp-el" lang="el">{wd(d, 'el-GR')}</span>
          </div>
        ))}
      </div>
      {weeks.map((week) => (
        <div key={week[0]} className="wp-cal-week" role="row">
          {week.map((d) => {
            const inMonth = d.startsWith(month);
            const list = inMonth ? byDay.get(d) ?? [] : [];
            const entries = list.map((x) => entryOf(x.item, x.record, boatOf(x.record)));
            const time = sumWashTimes(entries.map((e) => e.model));
            const dels = inMonth && showDeliveries ? deliveries.get(d) ?? [] : [];
            const weekend = (new Date(`${d}T00:00:00Z`).getUTCDay() + 6) % 7 >= 5;
            return (
              <div
                key={d}
                role="gridcell"
                className={`wp-day${inMonth ? '' : ' is-out'}${d === today ? ' is-today' : ''}${d < today ? ' is-past' : ''}${weekend ? ' is-weekend' : ''}${overDay === d ? ' is-over' : ''}`}
                aria-label={`${df.date(d, { weekday: 'long' })}: ${list.length ? plural(list.length, 'wash', 'washes') : 'no washes'}${time.counted ? `, ${washRangeText(time.total)} of washing` : ''}`}
                {...(inMonth ? dayDrop(d) : {})}
              >
                <div className="wp-day-hd">
                  <span className="wp-day-n">{Number(d.slice(8))}</span>
                  {time.counted > 0 ? (
                    <span className="wp-day-time" title={dayTimeText(time)}><IconClock size={11} />{washRangeText(time.total, false, true)}{time.unknown ? '+' : ''}</span>
                  ) : list.length > 0 && <span className="wp-day-load" aria-hidden="true">{list.map((x) => <i key={x.record.id} className={`wp-k-${x.record.kind}`} />)}</span>}
                </div>
                <div className="wp-day-list">{entries.map(chip)}</div>
                {dels.length > 0 && (
                  <div className="wp-del" title={`Delivery: ${dels.join(', ')}`}>
                    <IconShip size={12} /><span>{dels.length === 1 ? dels[0] : `${dels.length} deliveries`}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  // phone: the month as a list of the days that have washes (and today)
  const agenda = (() => {
    const days = [...new Set([...byDay.keys(), ...(today.startsWith(month) ? [today] : [])])].sort();
    if (!days.length) return <p className="wp-empty">No washes planned in {monthTitle(month, 'en-GB')} yet. Plan them from “To plan”.</p>;
    return (
      <ol className="wp-agenda">
        {days.map((d) => {
          const entries = (byDay.get(d) ?? []).map((x) => entryOf(x.item, x.record, boatOf(x.record)));
          const time = sumWashTimes(entries.map((e) => e.model));
          return (
            <li key={d} className={`wp-aday${d === today ? ' is-today' : ''}${d < today ? ' is-past' : ''}`}>
              <div className="wp-aday-hd">
                <b>{df.date(d, { weekday: 'long', year: 'auto' })}</b>
                <span className="wp-el" lang="el">{new Intl.DateTimeFormat('el-GR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`))}</span>
                {time.counted > 0 && <span className="wp-aday-time"><IconClock size={12} />{dayTimeText(time)}</span>}
              </div>
              <div className="wp-aday-list">
                {entries.map(chip)}
                {!entries.length && <span className="muted">Nothing planned</span>}
              </div>
            </li>
          );
        })}
      </ol>
    );
  })();

  const queuePanel = (
    <section className="wp-queue" aria-label="Washes to plan">
      <div className="wp-qhd">
        <h2>To plan <span className="wp-el" lang="el">Για προγραμματισμό</span></h2>
        <span className="wp-qcount">{allQueue.length}</span>
      </div>
      <div className="wp-qtools">
        <Segmented
          value={filter}
          onChange={setFilter}
          ariaLabel="Boats"
          options={[{ value: 'all', label: 'All' }, ...(canOv ? [{ value: 'prod' as const, label: 'Production' }] : []), ...(canSv ? [{ value: 'service' as const, label: 'Service' }] : [])]}
        />
        <SearchInput value={q} onChange={setQ} placeholder="Search boat / SAP" />
      </div>
      {queue.length ? <ul className="wp-qlist">{queue.map(queueCard)}</ul> : (
        <p className="wp-empty">{allQueue.length ? 'No boat matches.' : 'Every wash the boats need is planned. 🎉'}</p>
      )}
      {notNeeded.length > 0 && (
        <details className="wp-skipped">
          <summary><IconDropOff size={14} /> No washing required <span className="wp-el" lang="el">Δεν χρειάζεται</span><span className="wp-skipped-n">{notNeeded.length}</span></summary>
          <ul>
            {notNeeded.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => setOpenId(r.id)} title="Open it to undo">
                  {badge(r.kind)}<span className="wp-skipped-name">{r.boatName || r.boatKey}</span><small>{fmt(r.notNeededOn)}</small>
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
      {canOv && (
        <label className="wp-rule">
          Re-wash when the pre-delivery wash is more than
          <input className="field" type="number" min={1} max={90} value={rewashDays} onChange={(e) => setRewashDays(Math.min(90, Math.max(1, Number(e.target.value) || REWASH_AFTER_DAYS)))} />
          days before the delivery
        </label>
      )}
    </section>
  );

  return (
    <div className="wp">
      <PageHeader title="Washing Plan" meta={<span className="wp-el" lang="el">Πρόγραμμα πλυσίματος</span>}>
        <button className="btn btn-sm" disabled={ov.isFetching || sv.isFetching || washes.isFetching} onClick={() => { void ov.refetch(); void sv.refetch(); void washes.refetch(); }}>
          <SyncLabel busy={ov.isFetching || sv.isFetching || washes.isFetching} />
        </button>
      </PageHeader>

      {ov.error != null && <InlineError error={ov.error} />}
      {sv.error != null && <InlineError error={sv.error} />}
      {washes.error != null && <InlineError error={washes.error} />}
      {set.isError && <InlineError error={set.error} onDismiss={() => set.reset()} />}
      {exportErr != null && <InlineError error={exportErr} onDismiss={() => setExportErr(null)} />}

      {/* the month, its numbers, the key and the export, in one band */}
      <section className="wp-hero">
        <div className="wp-month">
          <button type="button" className="wp-nav" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month"><IconChevronLeft size={18} /></button>
          <div className="wp-month-t">
            <h2>{monthTitle(month, 'en-GB')}</h2>
            <span className="wp-el" lang="el">{monthTitle(month, 'el-GR')}</span>
          </div>
          <button type="button" className="wp-nav" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month"><IconChevronRight size={18} /></button>
          {month !== today.slice(0, 7) && <button type="button" className="btn btn-sm wp-today" onClick={() => setMonth(today.slice(0, 7))}>This month</button>}
        </div>
        <div className="wp-kpis">
          <div className="wp-kpi is-plan"><b>{stats.toPlan}</b><span>to plan</span><small>{stats.ready} ready now</small></div>
          <div className="wp-kpi is-month"><b>{stats.planned}</b><span>planned</span><small>{stats.plannedTime.counted ? <>this month · <span className="wp-nowrap" title={dayTimeText(stats.plannedTime)}>{washRangeText(stats.plannedTime.total)}</span></> : 'this month'}</small></div>
          <div className="wp-kpi is-done"><b>{stats.done}</b><span>done</span><small>this month</small></div>
          <div className={`wp-kpi is-due${stats.due + stats.late ? ' is-hot' : ''}`}><b>{stats.due + stats.late}</b><span>need attention</span><small>{stats.late ? `${stats.late} planned, not washed` : 'due within a week'}</small></div>
        </div>
        <div className="wp-export">
          <span className="wp-export-t">Schedule for the floor <span className="wp-el" lang="el">Πρόγραμμα για το συνεργείο</span></span>
          <Segmented value={lang} onChange={setLang} ariaLabel="Language" options={[{ value: 'both', label: 'EN + ΕΛ' }, { value: 'el', label: 'Ελληνικά' }, { value: 'en', label: 'English' }]} />
          <div className="wp-export-b">
            <button type="button" className="btn btn-sm btn-primary" onClick={doPrint}><IconPrinter size={14} /> Print / PDF</button>
            <button type="button" className="btn btn-sm" onClick={doExcel}><IconDownload size={14} /> Excel</button>
          </div>
        </div>
        <ul className="wp-legend" aria-label="The washes">
          {WASH_KINDS.filter((k) => (WASH_KIND_INFO[k].source === 'prod' ? canOv : canSv)).map((k) => (
            <li key={k}>{badge(k)}<span>{WASH_KIND_INFO[k].name}</span><span className="wp-el" lang="el">{WASH_KIND_INFO[k].nameEl}</span></li>
          ))}
          <li className="wp-legend-del">
            <label><input type="checkbox" checked={showDeliveries} onChange={(e) => setShowDeliveries(e.currentTarget.checked)} /> <IconShip size={13} /> Deliveries on the calendar</label>
          </li>
        </ul>
      </section>

      {loading && <LoadingState label="Loading the boats…" />}

      {!loading && (isPhone ? (
        <>
          <Segmented
            value={tab}
            onChange={setTab}
            ariaLabel="View"
            options={[{ value: 'month', label: 'Month' }, { value: 'queue', label: `To plan (${allQueue.length})` }]}
          />
          {tab === 'month' ? agenda : queuePanel}
        </>
      ) : (
        <div className="wp-body">
          {queuePanel}
          <section className="wp-main" aria-label="The month">
            {drag && <div className="wp-hint"><IconDrop size={14} /> Drop it on the day to wash it</div>}
            {calendar}
          </section>
        </div>
      ))}

      {open && (
        <WashDialog
          key={open.id}
          entry={open}
          editable={editable(open.source)}
          today={today}
          onChange={(v) => change(open, v)}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}
